"use client";

// ──────────────────────────────────────────────────────────────────────────
//  EquipmentTable — per-BiS-slot view of the character's live equipment.
//
//  Used on the dashboard so the user can see at a glance which slots are
//  already at Myth (and therefore excluded from catalyzed BiS recs) vs.
//  which still have Hero / Champion / Veteran gear pulling P% down.
//
//  No interactivity — read-only view of the equipment payload returned
//  by /api/characters/{id}/equipment.
// ──────────────────────────────────────────────────────────────────────────

import { BIS_SLOTS } from "@/lib/bis-slots";
import { classifyTrack, groupForBlizzardSlot, maxEquippedIlvl, TRACK_INFO } from "@/lib/myth";
import type { LootCache, GroupID } from "@/lib/types";
import type { EquippedItem } from "@/lib/myth";
import { cn } from "@/lib/utils";

interface Props {
  equipped: EquippedItem[];
  /** Loot cache is kept in props for parity with previous signature, but
   *  track classification no longer reads from it — it's per-character
   *  ratio-based. Cache is unused for now; left here in case we surface
   *  cache-tier-relative annotations later. */
  cache?: LootCache;
}

interface SlotRow {
  groupID: GroupID;
  label: string;
  /** Up to 2 items per group (RING, TRINKET). */
  items: EquippedItem[];
}

function buildRows(equipped: EquippedItem[]): SlotRow[] {
  const byGroup: Partial<Record<GroupID, EquippedItem[]>> = {};
  for (const eq of equipped) {
    const g = groupForBlizzardSlot(eq.slot);
    if (!g) continue;
    (byGroup[g] ??= []).push(eq);
  }
  return BIS_SLOTS.map((g) => ({
    groupID: g.id,
    label: g.label,
    items: byGroup[g.id] ?? [],
  }));
}

export function EquipmentTable({ equipped }: Props) {
  const rows = buildRows(equipped);
  // Compute the calibration anchor ONCE so every row gets bucketed
  // against the same max — otherwise an outlier row would shift its
  // own classification.
  const maxIlvl = maxEquippedIlvl(equipped);
  return (
    <ul className="divide-y divide-border">
      {rows.map((row) => (
        <li key={row.groupID} className="px-5 py-2.5 flex items-center gap-4">
          <span className="w-24 text-sm text-fg shrink-0">{row.label}</span>
          {row.items.length === 0 ? (
            <span className="text-xs text-fg-subtle italic">Empty</span>
          ) : (
            <div className="flex-1 flex flex-wrap items-center gap-x-6 gap-y-1.5">
              {row.items.map((it) => (
                <EquippedRowItem key={`${it.slot}-${it.itemID}`} item={it} maxIlvl={maxIlvl} />
              ))}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function EquippedRowItem({ item, maxIlvl }: { item: EquippedItem; maxIlvl: number }) {
  const track = classifyTrack(item, maxIlvl);
  const info = TRACK_INFO[track];
  return (
    <div className="flex items-center gap-3 min-w-0 flex-1">
      <span
        className="font-mono tabular text-sm text-fg-muted w-10 shrink-0"
        title={`${item.ilvl} item-level`}
      >
        {item.ilvl}
      </span>
      <span
        className={cn(
          "inline-flex items-center px-1.5 h-5 rounded-xs text-[10px] font-mono uppercase tracking-wider",
          "border shrink-0",
        )}
        style={{
          color: info.colorVar,
          borderColor: info.colorVar,
        }}
      >
        {info.label}
      </span>
      <span className="text-sm text-fg-muted truncate min-w-0">
        {item.name ?? `Item #${item.itemID}`}
      </span>
    </div>
  );
}
